import { useEffect, useCallback, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

interface ConfirmModalProps {
  open: boolean
  onClose: () => void
  title: string
  message: ReactNode
  warning?: string
  onConfirm: () => void
  onCancel?: () => void
  confirmText?: string
  cancelText?: string
  /** 为 true 时隐藏取消按钮，确认按钮占满整行（用于纯提示型弹窗） */
  hideCancel?: boolean
}

export default function ConfirmModal({
  open,
  onClose,
  title,
  message,
  warning,
  onConfirm,
  onCancel,
  confirmText,
  cancelText,
  hideCancel,
}: ConfirmModalProps) {
  const { t } = useTranslation()

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    },
    [onClose]
  )

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [open, handleKeyDown])

  if (!open) return null

  return createPortal(
    <div className="fixed inset-0 z-[100]">
      {/* Overlay */}
      <div
        className="fixed inset-0 animate-fade-in"
        style={{ background: 'rgba(0,0,0,0.6)' }}
        onClick={onClose}
      />

      {/* Sheet */}
      <div
        className="fixed bottom-0 left-0 right-0 animate-slide-up rounded-t-2xl px-5 pt-6 pb-8"
        style={{
          maxWidth: 430,
          margin: '0 auto',
          background: '#0C0C1C',
          border: '1px solid #1A1A38',
          borderBottom: 'none',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle bar */}
        <div className="flex justify-center mb-5">
          <div className="w-10 h-1 rounded-full" style={{ background: '#1A1A38' }} />
        </div>

        <h3 className="text-text font-semibold text-base mb-2">{title}</h3>
        <p className="text-text-muted text-sm mb-2">{message}</p>
        {warning && (
          <p className="text-xs mb-4 leading-relaxed" style={{ color: '#EF4444' }}>{warning}</p>
        )}
        {!warning && <div className="mb-4" />}

        <div className="flex gap-3">
          {!hideCancel && (
            <button
              onClick={() => {
                onCancel?.()
                onClose()
              }}
              className="flex-1 py-3 rounded-xl text-sm font-medium transition-colors"
              style={{ background: '#1A1A38', color: '#A1A1AA', border: '1px solid #1A1A38' }}
            >
              {cancelText ?? t('common.cancel')}
            </button>
          )}
          <button
            onClick={() => {
              onConfirm()
              onClose()
            }}
            className="flex-1 py-3 rounded-xl text-sm font-bold transition-colors"
            style={{ background: '#2E6BFF', color: '#FFFFFF' }}
          >
            {confirmText ?? t('common.confirm')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
